import { createReducer, on } from '@ngrx/store';
import { initialParameterState } from './parameter.state';
import * as ParameterActions from './parameter.actions';

export const parameterReducer = createReducer(
  initialParameterState,

  on(ParameterActions.loadAllParameters, state => ({
    ...state,
    loading: true,
    error: null
  })),

  on(ParameterActions.loadAllParametersSuccess, (state, { parameters }) => ({
    ...state,
    parameters,
    loading: false,
    error: null,
    cacheValid: true,
    lastFetched: Date.now(),
    cacheExpiry: Date.now() + 5 * 60 * 1000,
    forceRefresh: false
  })),

  on(ParameterActions.loadAllParametersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  on(ParameterActions.selectParameter, (state, { parameter }) => ({
    ...state,
    selectedParameter: parameter
  })),

  on(ParameterActions.createParameter, state => ({
    ...state,
    creating: true,
    error: null,
    cacheValid: false
  })),

  on(ParameterActions.createParameterSuccess, (state, { parameter }) => ({
    ...state,
    parameters: [...state.parameters, parameter],
    creating: false,
    error: null,
    cacheValid: false // Invalidate cache to ensure fresh data
  })),

  on(ParameterActions.createParameterFailure, (state, { error }) => ({
    ...state,
    creating: false,
    error
  })),

  on(ParameterActions.updateParameter, state => ({
    ...state,
    updating: true,
    error: null,
    cacheValid: false
  })),

  on(ParameterActions.updateParameterSuccess, (state, { parameter }) => ({
    ...state,
    parameters: state.parameters.map(p =>
      p.idParametro === parameter.idParametro ? parameter : p
    ),
    selectedParameter:
      state.selectedParameter?.idParametro === parameter.idParametro
        ? parameter
        : state.selectedParameter,
    updating: false,
    error: null
  })),

  on(ParameterActions.updateParameterFailure, (state, { error }) => ({
    ...state,
    updating: false,
    error
  })),

  on(ParameterActions.fetchParameterById, state => ({
    ...state,
    loadingParameterById: true,
    error: null
  })),

  on(ParameterActions.fetchParameterByIdSuccess, (state, { parameter }) => ({
    ...state,
    selectedParameter: parameter,
    loadingParameterById: false,
    error: null
  })),

  on(ParameterActions.fetchParameterByIdFailure, (state, { error }) => ({
    ...state,
    loadingParameterById: false,
    error
  })),

  on(ParameterActions.loadActiveParameters, state => ({
    ...state,
    loadingActiveParameters: true,
    error: null
  })),

  on(ParameterActions.loadActiveParametersSuccess, (state, { parameters }) => ({
    ...state,
    parameters,
    loadingActiveParameters: false,
    error: null,
    cacheValid: true,
    lastFetched: Date.now(),
    cacheExpiry: Date.now() + 5 * 60 * 1000,
    forceRefresh: false
  })),

  on(ParameterActions.loadActiveParametersFailure, (state, { error }) => ({
    ...state,
    loadingActiveParameters: false,
    error
  })),

  on(ParameterActions.loadInactiveParameters, state => ({
    ...state,
    loadingInactiveParameters: true,
    error: null
  })),

  on(ParameterActions.loadInactiveParametersSuccess, (state, { parameters }) => ({
    ...state,
    parameters,
    loadingInactiveParameters: false,
    error: null,
    cacheValid: true,
    lastFetched: Date.now(),
    cacheExpiry: Date.now() + 5 * 60 * 1000,
    forceRefresh: false
  })),

  on(ParameterActions.loadInactiveParametersFailure, (state, { error }) => ({
    ...state,
    loadingInactiveParameters: false,
    error
  })),

  // Cache management
  on(ParameterActions.invalidateCache, state => ({
    ...state,
    cacheValid: false,
    forceRefresh: false
  })),

  on(ParameterActions.forceRefresh, state => ({
    ...state,
    forceRefresh: true,
    cacheValid: false
  })),

  on(ParameterActions.setCacheValid, (state, { expiry }) => ({
    ...state,
    cacheValid: true,
    lastFetched: Date.now(),
    cacheExpiry: expiry,
    forceRefresh: false
  })),

  on(ParameterActions.clearCache, state => ({
    ...state,
    parameters: [],
    selectedParameter: null,
    cacheValid: false,
    lastFetched: null,
    cacheExpiry: null,
    forceRefresh: false
  })),

  // Parameter Detail handlers
  on(ParameterActions.createParameterDetail, state => ({
    ...state,
    error: null
  })),

  on(ParameterActions.createParameterDetailSuccess, (state, { parameterId, detail }) => ({
    ...state,
    parameters: state.parameters.map(parameter =>
      parameter.idParametro === parameterId
        ? {
            ...parameter,
            parametroDetalles: [...(parameter.parametroDetalles || []), detail]
          }
        : parameter
    ),
    selectedParameter:
      state.selectedParameter?.idParametro === parameterId
        ? {
            ...state.selectedParameter,
            parametroDetalles: [...(state.selectedParameter.parametroDetalles || []), detail]
          }
        : state.selectedParameter,
    error: null
  })),

  on(ParameterActions.createParameterDetailFailure, (state, { error }) => ({
    ...state,
    error
  })),

  on(ParameterActions.updateParameterDetail, state => ({
    ...state,
    error: null
  })),

  on(ParameterActions.updateParameterDetailSuccess, (state, { parameterId, detail }) => ({
    ...state,
    parameters: state.parameters.map(parameter =>
      parameter.idParametro === parameterId
        ? {
            ...parameter,
            parametroDetalles: (parameter.parametroDetalles || []).map(d =>
              d.idParametroDetalle === detail.idParametroDetalle ? detail : d
            )
          }
        : parameter
    ),
    selectedParameter:
      state.selectedParameter?.idParametro === parameterId
        ? {
            ...state.selectedParameter,
            parametroDetalles: (state.selectedParameter.parametroDetalles || []).map(d =>
              d.idParametroDetalle === detail.idParametroDetalle ? detail : d
            )
          }
        : state.selectedParameter,
    error: null
  })),

  on(ParameterActions.updateParameterDetailFailure, (state, { error }) => ({
    ...state,
    error
  })),

  on(ParameterActions.clearParameterError, state => ({
    ...state,
    error: null
  }))
);
